// Default BrokerStore impls.
//
//   MemoryBrokerStore       — pure in-memory; used in tests and as a
//                              fallback when localStorage is denied
//                              (private browsing on some platforms).
//   LocalStorageBrokerStore — wraps MemoryBrokerStore and writes the
//                              snapshot + event log to window.localStorage
//                              after every routed envelope so a tab
//                              reload reconstructs the session.
//
// Both implement `exportSession(sessionId)` which emits the same JSON
// shape the C++ controller's REST export uses: a single document with
// the session row, participant bindings, and the verbatim event log.
// The export is plain JSON (not JSONL) for ease of consumption by
// downstream tooling and clipboard workflows.

import type {
  BrokerSnapshot,
  BrokerStore,
  EventLogRow,
  NodeRow,
  ParticipantRow,
  ResourceRow,
  SessionExport,
  SessionRow,
} from './types';

const EMPTY_SNAPSHOT: BrokerSnapshot = {
  version: 1,
  nodes: [],
  sessions: [],
  participants: [],
  events: [],
};

export class MemoryBrokerStore implements BrokerStore {
  protected snapshot: BrokerSnapshot = structuredCloneSafe(EMPTY_SNAPSHOT);
  // Keep the full event log separately so the snapshot can tail-truncate
  // while exports still return everything.
  protected eventLog: EventLogRow[] = [];
  // Writable resource rows (studies, presets), keyed by `${resource}:${id}`
  // so we can fan delete/list back out to the broker without scanning.
  protected resources: Map<string, ResourceRow> = new Map();

  load(): Promise<BrokerSnapshot | null> | BrokerSnapshot | null {
    return this.snapshot;
  }

  save(snapshot: BrokerSnapshot): void {
    this.snapshot = snapshot;
  }

  appendEvent(row: EventLogRow): void {
    this.eventLog.push(row);
  }

  saveResource(row: ResourceRow): void {
    this.resources.set(resourceKey(row.resource, row.id), row);
  }

  deleteResource(resource: string, id: string): void {
    this.resources.delete(resourceKey(resource, id));
  }

  loadResources(): Promise<ResourceRow[]> | ResourceRow[] {
    return [...this.resources.values()];
  }

  exportSession(sessionId: string): SessionExport {
    const session = this.snapshot.sessions.find((s) => s.sessionId === sessionId);
    const participants = this.snapshot.participants.filter(
      (p) => p.sessionId === sessionId,
    );
    const events = this.eventLog.filter((e) => e.sessionId === sessionId);
    const result: SessionExport = {
      sessionId,
      participants,
      events,
      exportedAt: new Date().toISOString(),
    };
    if (session) result.session = session;
    return result;
  }

  /** Test/debug only: current snapshot reference. */
  current(): BrokerSnapshot {
    return this.snapshot;
  }

  /** Test/debug only: full event log. */
  allEvents(): EventLogRow[] {
    return this.eventLog;
  }
}

export interface LocalStorageBrokerStoreOptions {
  /** Storage key prefix. Defaults to 'usability-suite:broker'. */
  keyPrefix?: string;
  /** Maximum events to retain in the rolling snapshot. Default 5000. */
  maxEvents?: number;
  /** Storage backend; defaults to window.localStorage. Tests inject a fake. */
  storage?: StorageLike;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const SNAPSHOT_SUFFIX = ':snapshot';
const EVENTS_SUFFIX = ':events';
const RESOURCES_SUFFIX = ':resources';

export class LocalStorageBrokerStore extends MemoryBrokerStore {
  private readonly keyPrefix: string;
  private readonly maxEvents: number;
  private readonly storage: StorageLike | null;

  constructor(opts: LocalStorageBrokerStoreOptions = {}) {
    super();
    this.keyPrefix = opts.keyPrefix ?? 'usability-suite:broker';
    this.maxEvents = opts.maxEvents ?? 5000;
    this.storage = opts.storage ?? pickStorage();
    this.hydrate();
  }

  override save(snapshot: BrokerSnapshot): void {
    super.save(snapshot);
    this.scheduleFlush();
  }

  override appendEvent(row: EventLogRow): void {
    super.appendEvent(row);
    this.scheduleFlush();
  }

  override saveResource(row: ResourceRow): void {
    super.saveResource(row);
    this.scheduleFlush();
  }

  override deleteResource(resource: string, id: string): void {
    super.deleteResource(resource, id);
    this.scheduleFlush();
  }

  /** Wipe persisted state. Caller-driven; the broker never calls this itself. */
  clear(): void {
    if (!this.storage) return;
    this.storage.removeItem(this.keyPrefix + SNAPSHOT_SUFFIX);
    this.storage.removeItem(this.keyPrefix + EVENTS_SUFFIX);
    this.storage.removeItem(this.keyPrefix + RESOURCES_SUFFIX);
  }

  private hydrate(): void {
    if (!this.storage) return;
    const snapRaw = this.storage.getItem(this.keyPrefix + SNAPSHOT_SUFFIX);
    const eventsRaw = this.storage.getItem(this.keyPrefix + EVENTS_SUFFIX);
    const resourcesRaw = this.storage.getItem(this.keyPrefix + RESOURCES_SUFFIX);
    if (snapRaw) {
      try {
        const parsed = JSON.parse(snapRaw) as BrokerSnapshot;
        if (parsed && parsed.version === 1) this.snapshot = parsed;
      } catch {
        // ignore corrupted snapshot — start fresh.
      }
    }
    if (eventsRaw) {
      try {
        const parsed = JSON.parse(eventsRaw) as EventLogRow[];
        if (Array.isArray(parsed)) this.eventLog = parsed;
      } catch {
        // ignore corrupted event log.
      }
    }
    if (resourcesRaw) {
      try {
        const parsed = JSON.parse(resourcesRaw) as ResourceRow[];
        if (Array.isArray(parsed)) {
          for (const row of parsed) {
            if (row && typeof row.resource === 'string' && typeof row.id === 'string') {
              this.resources.set(resourceKey(row.resource, row.id), row);
            }
          }
        }
      } catch {
        // ignore corrupted resource table — designer / facilitator
        // can re-author or re-import.
      }
    }
  }

  private scheduleFlush(): void {
    // Synchronous on purpose. The earlier microtask-batched version
    // would silently lose the most recent save when the SharedWorker
    // was evicted between scheduling the microtask and the runtime
    // dispatching it (Chrome doesn't expose a `terminate` hook on
    // the worker, so there's no place to drain the batch). The
    // localStorage writes are cheap for the broker's traffic
    // shape — a single chat session is low-thousands of bytes per
    // write — and the consistency of "save returned = data is on
    // disk" beats the lost-write hazard the batch path created.
    this.flush();
  }

  private flush(): void {
    if (!this.storage) return;
    try {
      // Trim the event tail to maxEvents to keep the snapshot bounded.
      // The export path reads from the unbounded eventLog still held in
      // memory; this trim only affects what survives a reload.
      const tail = this.eventLog.slice(-this.maxEvents);
      this.storage.setItem(
        this.keyPrefix + SNAPSHOT_SUFFIX,
        JSON.stringify(this.snapshot),
      );
      this.storage.setItem(this.keyPrefix + EVENTS_SUFFIX, JSON.stringify(tail));
      // Resources are bounded by what the user authored (handful of
      // studies, presets), not by event throughput, so no tail trim.
      this.storage.setItem(
        this.keyPrefix + RESOURCES_SUFFIX,
        JSON.stringify([...this.resources.values()]),
      );
    } catch {
      // QuotaExceededError or storage-denied. Silently degrade to
      // in-memory; the next successful write will catch up.
    }
  }
}

function resourceKey(resource: string, id: string): string {
  return resource + ':' + id;
}

function pickStorage(): StorageLike | null {
  try {
    if (typeof globalThis !== 'undefined') {
      const g = globalThis as { localStorage?: StorageLike };
      if (g.localStorage) return g.localStorage;
    }
  } catch {
    // Access to localStorage can throw in sandboxed iframes.
  }
  return null;
}

function structuredCloneSafe<T>(v: T): T {
  if (typeof structuredClone === 'function') return structuredClone(v);
  return JSON.parse(JSON.stringify(v)) as T;
}

/** Convenience: serialize a SessionExport as a downloadable JSON string. */
export function serializeExport(exp: SessionExport): string {
  return JSON.stringify(exp, null, 2);
}

export type { BrokerSnapshot, NodeRow, ParticipantRow, SessionRow, EventLogRow };
